import { KeyboardEvent, PointerEvent, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { useTranslation } from '@frontend/i18n';
import { genUUIDV4 } from '@frontend/string';
import { Button, InlineNotification, Text, useTooltip } from '@frontend/design-system';
import { MAX_PDF_BYTES } from '../../signature/constants';
import { reportSignatureError } from '../../signature/monitoring';
import { createSignatureTimestamp, formatSignatureTimestamp } from '../../signature/timestamp';
import { useSignatureUpload } from '../../signature/use-signature-upload';
import { SavedPdfSignature } from '../../store';
import { PdfSignError, generateSignedPdf } from './generate-signed-pdf';
import {
  footerHelperStyle,
  footerNoticeStyle,
  footerStyle,
  placementControlsStyle,
  placementStampStyle,
  placementStyle,
} from './pdf-field.styles';
import {
  NUDGE_STEP,
  NUDGE_STEP_LARGE,
  PdfPageSize,
  PdfPlacement,
  STAMP_FONT_SIZE,
  STAMP_LINE_HEIGHT,
  createInitialPlacement,
  getPlacementSize,
  getStampTextLayout,
  movePlacement,
  nudgePlacement,
  resizePlacement,
} from './pdf-placement';
import {
  PdfSignEditorError,
  getPdfSignEditorStep,
  initialPdfSignEditorState,
  pdfSignEditorReducer,
} from './pdf-sign-editor.reducer';
import { PdfSignatureModal } from './pdf-signature-modal';
import { PdfViewer } from './pdf-viewer';
import { useDragHint } from './use-drag-hint';

interface PdfSignEditorProps {
  fieldKey: string;
  formId?: string;
  originalBlob: Blob;
  doc: PDFDocumentProxy;
  pages: PdfPageSize[];
  onCommit: (signedAttachmentId: string) => void;
  onGeneratingChange: (isGenerating: boolean) => void;
  onRenderError?: (error: unknown) => void;
}

export const PdfSignEditor = ({
  fieldKey,
  formId,
  originalBlob,
  doc,
  pages,
  onCommit,
  onGeneratingChange,
  onRenderError,
}: PdfSignEditorProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const uploadSignedPdf = useSignatureUpload(fieldKey);
  const [state, dispatch] = useReducer(pdfSignEditorReducer, initialPdfSignEditorState);
  const step = getPdfSignEditorStep(state);
  const isGenerating = step === 'generating';

  const scrollRootRef = useRef<HTMLDivElement>(null);
  const pageElements = useRef(new Map<number, HTMLDivElement>());
  const setPageRef = useCallback((pageIndex: number, element: HTMLDivElement | null) => {
    if (element) pageElements.current.set(pageIndex, element);
    else pageElements.current.delete(pageIndex);
  }, []);

  /** The page (and vertical position on it) at the middle of the scroll area, where new signatures land. */
  const getVisibleAnchor = () => {
    const rootRect = scrollRootRef.current?.getBoundingClientRect();
    if (!rootRect) return { pageIndex: 0, centerY: 0.5 };
    const middle = rootRect.top + rootRect.height / 2;
    for (const [pageIndex, element] of pageElements.current) {
      const rect = element.getBoundingClientRect();
      if (rect.height > 0 && middle >= rect.top && middle <= rect.bottom) {
        return { pageIndex, centerY: (middle - rect.top) / rect.height };
      }
    }
    return { pageIndex: 0, centerY: 0.5 };
  };

  const insertSignature = (signature: SavedPdfSignature) => {
    const editing = state.placements.find((placement) => placement.id === state.editingId);
    const anchor = editing
      ? { pageIndex: editing.pageIndex, centerY: editing.y + getPlacementSize(editing).h / 2 }
      : getVisibleAnchor();
    const page = pages[anchor.pageIndex];
    if (!page) return;
    const placement = createInitialPlacement({
      id: genUUIDV4(),
      pageIndex: anchor.pageIndex,
      page,
      signature,
      centerY: anchor.centerY,
    });
    dispatch({
      type: 'INSERT',
      placement: editing ? movePlacement(placement, editing.pageIndex, editing.x, editing.y, page, page) : placement,
    });
  };

  /** Resolves the page under the dropped signature's centre (nearest page if it lands in a gap). */
  const handleDrop = (placement: PdfPlacement, rect: DOMRect) => {
    const centerY = rect.top + rect.height / 2;
    let target: { pageIndex: number; rect: DOMRect; distance: number } | undefined;
    for (const [pageIndex, element] of pageElements.current) {
      const pageRect = element.getBoundingClientRect();
      const distance = centerY < pageRect.top ? pageRect.top - centerY : Math.max(0, centerY - pageRect.bottom);
      if (!target || distance < target.distance) target = { pageIndex, rect: pageRect, distance };
    }
    const sourcePage = pages[placement.pageIndex];
    const page = target && pages[target.pageIndex];
    if (!target || !sourcePage || !page || target.rect.width <= 0 || target.rect.height <= 0) return;
    dispatch({
      type: 'UPDATE',
      placement: movePlacement(
        placement,
        target.pageIndex,
        (rect.left - target.rect.left) / target.rect.width,
        (rect.top - target.rect.top) / target.rect.height,
        sourcePage,
        page
      ),
    });
  };

  const handleDone = async () => {
    if (originalBlob.size > MAX_PDF_BYTES) {
      dispatch({ type: 'GENERATE_FAILED', error: 'too-large' });
      return;
    }
    dispatch({ type: 'GENERATE_START' });
    onGeneratingChange(true);
    let operation: 'generate' | 'upload' = 'generate';
    try {
      const signedAt = createSignatureTimestamp();
      const bytes = await generateSignedPdf({
        original: new Uint8Array(await originalBlob.arrayBuffer()),
        placements: state.placements,
        signedAt,
        labels: {
          signedBy: (name) => t('Digitally signed by {{name}}', { name, interpolation: { escapeValue: false } }),
          date: (timestamp) => t('Date: {{timestamp}}', { timestamp, interpolation: { escapeValue: false } }),
        },
      });
      const file = new File([bytes as BlobPart], `${fieldKey}-signed.pdf`, { type: 'application/pdf' });

      operation = 'upload';
      const mediaId = await uploadSignedPdf(file, { replace: true });
      onGeneratingChange(false);
      onCommit(mediaId);
    } catch (error) {
      const reason: PdfSignEditorError =
        error instanceof PdfSignError && error.reason === 'password' ? 'password' : operation;
      dispatch({ type: 'GENERATE_FAILED', error: reason });
      onGeneratingChange(false);
      reportSignatureError(error, { feature: 'pdf-sign', operation, fieldKey, formId });
    }
  };

  // The footer keeps showing the underlying step while the signature modal is open on top of it.
  const footerStep = step === 'drawing' ? getPdfSignEditorStep({ ...state, mode: 'idle' }) : step;

  const errorMessages: Record<PdfSignEditorError, string> = {
    generate: t('Unable to save the signed document. Please try again.'),
    upload: t('Failed to upload the signed document. Please try again.'),
    password: t('This document is password protected and cannot be signed.'),
    'too-large': t('This document is too large to sign.'),
  };

  return (
    <>
      <PdfViewer
        ref={scrollRootRef}
        doc={doc}
        pages={pages}
        pageRef={setPageRef}
        onRenderError={onRenderError}
        renderPageOverlay={(pageIndex) =>
          state.placements
            .filter((placement) => placement.pageIndex === pageIndex)
            .map((placement) => (
              <PlacementOverlay
                key={placement.id}
                placement={placement}
                page={pages[pageIndex]}
                isActive={state.activeId === placement.id}
                disabled={isGenerating || step === 'drawing'}
                onActivate={() => dispatch({ type: 'ACTIVATE', id: placement.id })}
                onChange={(next) => dispatch({ type: 'UPDATE', placement: next })}
                onDrop={(rect) => handleDrop(placement, rect)}
                onRemove={() => dispatch({ type: 'REMOVE', id: placement.id })}
              />
            ))
        }
      />

      <div css={footerStyle}>
        {state.error && (
          <InlineNotification css={footerNoticeStyle} status='error' message={errorMessages[state.error]} />
        )}
        {footerStep === 'empty' && (
          <Button
            variant='primary'
            size='large'
            iconName='signature'
            onClick={() => dispatch({ type: 'OPEN_DRAWING' })}
          >
            {t('Add Signature')}
          </Button>
        )}
        {footerStep === 'placing' && (
          <>
            <Text as='span' size='small' color='light' css={footerHelperStyle}>
              {t('Drag it to the signature field')}
            </Text>
            <Button variant='secondary' size='large' iconName='edit' onClick={() => dispatch({ type: 'EDIT_ACTIVE' })}>
              {t('Edit Signature')}
            </Button>
            <Button variant='primary' size='large' iconName='check' onClick={() => dispatch({ type: 'FINISH' })}>
              {t('Finish & Sign')}
            </Button>
          </>
        )}
        {(footerStep === 'placed' || footerStep === 'generating') && (
          <>
            <Button
              variant='secondary'
              size='large'
              iconName='plus'
              disabled={isGenerating}
              onClick={() => dispatch({ type: 'OPEN_DRAWING' })}
            >
              {t('Add Signature')}
            </Button>
            <Button variant='primary' size='large' loading={isGenerating} onClick={handleDone}>
              {isGenerating ? t('Saving the document...') : t("I'm done")}
            </Button>
          </>
        )}
      </div>

      <PdfSignatureModal
        show={step === 'drawing'}
        fieldKey={fieldKey}
        onSave={insertSignature}
        onClose={() => dispatch({ type: 'CANCEL_DRAWING' })}
      />
    </>
  );
};

// Tooltip gap above the signature: tall enough to clear the resize/remove toolbar (see placementControlsStyle).
const PLACEMENT_HINT_OFFSET = 48;

interface PlacementOverlayProps {
  placement: PdfPlacement;
  page: PdfPageSize;
  isActive: boolean;
  disabled: boolean;
  onActivate: () => void;
  onChange: (placement: PdfPlacement) => void;
  onDrop: (rect: DOMRect) => void;
  onRemove: () => void;
}

const PlacementOverlay = ({
  placement,
  page,
  isActive,
  disabled,
  onActivate,
  onChange,
  onDrop,
  onRemove,
}: PlacementOverlayProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const ref = useRef<HTMLDivElement | null>(null);
  const { showHint, markDragged } = useDragHint();
  // Offset clears the resize/remove toolbar above the signature so its controls stay clickable.
  // Not dismissable by outside clicks: the hint only goes away once the patient actually drags the signature.
  const {
    Tooltip,
    tooltipProps,
    triggerProps: { ref: setTooltipReference },
    setOpen: setHintOpen,
  } = useTooltip({ trigger: 'click', placement: 'top', enableDismiss: false, offset: PLACEMENT_HINT_OFFSET });
  const setRefs = useCallback(
    (element: HTMLDivElement | null) => {
      ref.current = element;
      setTooltipReference(element);
    },
    [setTooltipReference]
  );
  const isHintVisible = showHint && isActive && !disabled;
  useEffect(() => {
    setHintOpen(isHintVisible);
  }, [isHintVisible, setHintOpen]);
  const drag = useRef<{ pointerId: number; startX: number; startY: number; moved: boolean } | null>(null);
  const stampMeasureRef = useRef<HTMLDivElement>(null);
  const [pageCssWidth, setPageCssWidth] = useState(0);
  const [stampLayout, setStampLayout] = useState({ left: 0, size: STAMP_FONT_SIZE });
  const previewDate = useMemo(() => formatSignatureTimestamp(new Date()), []);
  const { w, h } = getPlacementSize(placement);
  const signedByLabel = t('Digitally signed by {{name}}', {
    name: placement.signedBy,
    interpolation: { escapeValue: false },
  });
  const dateLabel = t('Date: {{timestamp}}', { timestamp: previewDate, interpolation: { escapeValue: false } });

  // Stamp text is sized in PDF points; convert using the rendered page width.
  useEffect(() => {
    const parent = ref.current?.parentElement;
    if (!parent) return;
    setPageCssWidth(parent.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => setPageCssWidth(entry.contentRect.width));
    observer.observe(parent);
    return () => observer.disconnect();
  }, []);
  const cssPerPt = page.width > 0 ? pageCssWidth / page.width : 1;

  // Measure at the default size, then mirror the export layout while keeping both preview lines in sync.
  useEffect(() => {
    const measure = stampMeasureRef.current;
    if (!measure || pageCssWidth <= 0 || page.width <= 0 || cssPerPt <= 0) return;
    const textWidth = measure.scrollWidth / cssPerPt;
    const layout = getStampTextLayout(placement.x * page.width, page.width, textWidth);
    const nextLayout = {
      left: (layout.x - placement.x * page.width) * cssPerPt,
      size: layout.size,
    };
    setStampLayout((current) =>
      Math.abs(current.left - nextLayout.left) < 0.1 && Math.abs(current.size - nextLayout.size) < 0.01
        ? current
        : nextLayout
    );
  }, [cssPerPt, dateLabel, page.width, pageCssWidth, placement.x, signedByLabel]);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled || !isActive || event.button !== 0) return;
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, moved: false };
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId || !ref.current) return;
    // A second finger turned this into a pinch-zoom: drop the drag so the signature stays put.
    if (ref.current.closest('[data-pinching="true"]')) {
      drag.current = null;
      ref.current.style.transform = '';
      ref.current.style.zIndex = '';
      return;
    }
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    if (!state.moved && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) {
      state.moved = true;
      // Hide the hint as soon as the drag starts rather than waiting for the drop.
      markDragged();
    }
    // Move via transform during the drag; commit once on drop to avoid re-rendering every frame.
    ref.current.style.transform = `translate(${dx}px, ${dy}px)`;
    ref.current.style.zIndex = '1';
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>, commit: boolean) => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId || !ref.current) return;
    drag.current = null;
    const rect = ref.current.getBoundingClientRect();
    ref.current.style.transform = '';
    ref.current.style.zIndex = '';
    if (commit && state.moved) {
      onDrop(rect);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled || event.target !== event.currentTarget) return;
    if (!isActive) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate();
      }
      return;
    }
    const step = event.shiftKey ? NUDGE_STEP_LARGE : NUDGE_STEP;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    if (moves[event.key]) {
      event.preventDefault();
      onChange(nudgePlacement(placement, moves[event.key][0], moves[event.key][1], page));
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      onChange(resizePlacement(placement, 1, page));
    } else if (event.key === '-') {
      event.preventDefault();
      onChange(resizePlacement(placement, -1, page));
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      onRemove();
    }
  };

  return (
    <div
      ref={setRefs}
      css={placementStyle}
      data-active={isActive}
      style={{
        left: `${placement.x * 100}%`,
        top: `${placement.y * 100}%`,
        width: `${w * 100}%`,
        height: `${h * 100}%`,
      }}
      role='group'
      tabIndex={disabled ? -1 : 0}
      aria-label={
        isActive
          ? t('Signature. Drag or use arrow keys to move, plus and minus to resize, Delete to remove.')
          : t('Signature. Press Enter to edit its position.')
      }
      onClick={() => !disabled && !isActive && onActivate()}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={(event) => endDrag(event, true)}
      onPointerCancel={(event) => endDrag(event, false)}
    >
      <img src={placement.dataUrl} alt='' draggable={false} />
      <div
        css={placementStampStyle}
        style={{
          left: stampLayout.left,
          fontSize: stampLayout.size * cssPerPt,
          lineHeight: `${STAMP_LINE_HEIGHT * cssPerPt}px`,
        }}
        aria-hidden
      >
        <div>{signedByLabel}</div>
        <div>{dateLabel}</div>
      </div>
      <div
        ref={stampMeasureRef}
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: '100%',
          width: 'max-content',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          fontFamily: 'Helvetica, Arial, sans-serif',
          fontSize: STAMP_FONT_SIZE * cssPerPt,
          lineHeight: `${STAMP_LINE_HEIGHT * cssPerPt}px`,
          pointerEvents: 'none',
        }}
      >
        <div>{signedByLabel}</div>
        <div>{dateLabel}</div>
      </div>
      <Tooltip {...tooltipProps}>{t('Drag your signature onto the signature line in the document.')}</Tooltip>
      {isActive && !disabled && (
        <div css={placementControlsStyle}>
          <Button
            variant='tertiary'
            size='small'
            iconName='minus'
            label={t('Make signature smaller')}
            onClick={() => onChange(resizePlacement(placement, -1, page))}
          />
          <Button
            variant='tertiary'
            size='small'
            iconName='plus'
            label={t('Make signature larger')}
            onClick={() => onChange(resizePlacement(placement, 1, page))}
          />
          <Button
            variant='tertiary'
            size='small'
            iconName='trash'
            destructive
            label={t('Remove signature')}
            onClick={onRemove}
          />
        </div>
      )}
    </div>
  );
};
